import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EntityAliases, EntityTypes } from '../../constants';
import APIService from '../APIService';
import { useAddFoldersToStories } from './usePosts';
import { useNavigate, useParams } from 'react-router-dom';
import { type ChildBoard } from '../../types';
import { fetchAuthSession } from 'aws-amplify/auth';

const useSetBoardDisplayOption = ({ entityId, boardId }: { entityId: string; boardId: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, value }: { name: string; value: string | number | boolean }) => {
      const url = `/${EntityTypes.BOARD}/${entityId}/folders/${boardId}`;
      const res = await APIService.PUT(url, undefined, { settings: { [name]: value } });
      return res.data;
    },
    onMutate: async ({ name, value }: { name: string; value: string | number | boolean }) => {
      await queryClient.cancelQueries({ queryKey: ['entity', entityId, EntityTypes.BOARD] });
      const previousData = queryClient.getQueryData(['entity', entityId, EntityTypes.BOARD]);
      queryClient.setQueryData(['entity', entityId, EntityTypes.BOARD], (old: any) => {
        return {
          ...old,
          folders: {
            ...old.folders,
            [boardId]: {
              ...old.folders[boardId],
              settings: {
                ...old.folders[boardId].settings,
                [name]: value,
              },
            },
          },
        };
      });
      return { previousData };
    },
    onError: (_, variables, context: any) => {
      queryClient.setQueryData(['entity', entityId, EntityTypes.BOARD], context.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['entity', entityId, EntityTypes.BOARD] });
    },
  });
};

const useSetBoardMetaOption = ({ entityId, boardId }: { entityId: string; boardId: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, value }: { name: string; value: string | number | boolean }) => {
      const url = `/${EntityTypes.BOARD}/${entityId}/folders/${boardId}`;
      const res = await APIService.PUT(url, undefined, { [name]: value });
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] });
    },
  });
};

const useGetFolderInfo = ({ entityId }: { entityId: string | undefined }) => {
  return useQuery({
    queryKey: ['folderInfo', entityId],
    queryFn: async () => {
      const url = `/${EntityTypes.BOARD}/${entityId}`;
      const res = await APIService.GET(url);
      const foldersObject: Record<string, ChildBoard> = res.data.folders;
      const currentFolders = Object.values(foldersObject);
      return currentFolders;
    },
  });
};

const useAddFolder = ({ entityId, queryKey }: { entityId: string; queryKey?: unknown[] }) => {
  const queryClient = useQueryClient();
  const addFoldersToStories = useAddFoldersToStories({ entityId, entityType: EntityTypes.BOARD, queryKey });
  const navigate = useNavigate();
  const { orgPath = '' } = useParams();
  return useMutation({
    mutationFn: async ({ name }: { name: string; storyIds?: string[] }) => {
      const url = `/${EntityTypes.BOARD}/${entityId}/folders`;
      const res = await APIService.POST(url, undefined, { name });
      return res.data;
    },
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(['entity', entityId, EntityTypes.BOARD], (old: any) => {
        return {
          ...old,
          folders: {
            ...old.folders,
            [data.folderId]: {
              ...data.entity.folders[data.folderId],
            },
          },
        };
      });
      if (variables.storyIds) {
        await addFoldersToStories({ storyIds: variables.storyIds, folderIds: [data.folderId] });
        navigate(`/${orgPath}/${EntityAliases.BOARD}/${entityId}/board/${data.folderId}`);
      }
    },
  });
};

const useDeleteFolder = ({ entityId }: { entityId: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (folderId: string) => {
      const url = `/${EntityTypes.BOARD}/${entityId}/folders/${folderId}`;
      await APIService.DELETE(url);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['folderInfo', entityId] });
    },
  });
};

const useUploadHashtagImageAction = (entityId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileList, folderId }: { fileList: File[]; folderId: string }) => {
      try {
        const authToken = (await fetchAuthSession()).tokens?.idToken?.toString() || '';
        const headers = {
          Authorization: authToken,
        };
        const formData = new FormData();
        formData.append('file', fileList[0]);
        return await APIService.POST(`/board/${entityId}/folders/${folderId}/coverImage`, undefined, formData, headers);
      } catch (error) {
        throw error
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folder', entityId] });
      await queryClient.invalidateQueries({ queryKey: ['entity'] });
    },
  });
};

const useRemoveHashtagImageAction = (entityId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ folderId }: { folderId: string }) => {
      return await APIService.DELETE(`/board/${entityId}/folders/${folderId}/coverImage`, undefined);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folder', entityId] });
      await queryClient.invalidateQueries({ queryKey: ['entity'] });
    },
  });
};

const usePublishFolder = ({ entityId }: { entityId: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ folderId, publish }: { folderId: string; publish: boolean }) => {
      const url = `/${EntityTypes.BOARD}/${entityId}/folders/${folderId}/status`;
      const res = await APIService.PUT(url, undefined, { published: publish });
      return res.data;
    },
    onSuccess: (_, { folderId, publish }) => {
      queryClient.invalidateQueries({ queryKey: ['folderInfo', entityId] });
      queryClient.setQueryData(['entity', entityId, EntityTypes.BOARD], (old: any) => {
        return {
          ...old,
          folders: {
            ...old.folders,
            [folderId]: {
              ...old.folders[folderId],
              published: publish,
            },
          },
        };
      });
    },
  });
};

export {
  useSetBoardDisplayOption,
  useSetBoardMetaOption,
  useGetFolderInfo,
  useAddFolder,
  useDeleteFolder,
  useUploadHashtagImageAction,
  useRemoveHashtagImageAction,
  usePublishFolder,
};
